/* Copyright (c) 2020 Paul Nebel */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';

import MetronomeMachine from '../core/utils/metronome-machine';

// can't define these in the state of this component as updating them will cause an infinite loop
const metroState = {};

class Metronome extends React.Component {
    static getDerivedStateFromProps(nextProps, prevState) {
        const {
            playerLoopDuration,
            playerMachineState,
            playHeadIndex,
            playHeadLength,
            playHeadComplete,
            playHeadRepeat,
            bpm,
            metroVolume,
        } = nextProps;

        // The playerMachine and the metronomeMachine are both notified at exactly the same time
        // when the loop ends.  This means that the metronome will already have 'told' the metro-indicator
        // to flash before the play head has the chance to let the rest of the components know that the
        // sequence has come to an end.  We therefore have to give the metronome advanced warning...
        if (playHeadIndex === playHeadLength - 1 && !playHeadRepeat) {
            prevState.metronomeMachine.finalIndex = true;
        } else {
            prevState.metronomeMachine.finalIndex = false;
        }

        if (playerLoopDuration !== prevState.playerLoopDuration) {
            prevState.metronomeMachine.loopDuration = playerLoopDuration;
        }

        if (playerMachineState !== prevState.playerMachineState) {
            switch (playerMachineState) {
                case 'paused':
                    prevState.metronomeMachine.pauseTimer();
                    break;
                case 'started':
                    prevState.metronomeMachine.startTimer();
                    break;
                case 'stopped':
                    prevState.metronomeMachine.resetTimer();
                    break;
                default:
                // do nothing
            }
        }

        if (playHeadComplete !== prevState.playHeadComplete) {
            prevState.metronomeMachine.resetTimer();
        }

        const newState = {
            ...prevState,
            playerLoopDuration,
            playerMachineState,
            playHeadComplete,
            bpm,
            metroVolume,
        };

        if (newState.bpm !== prevState.bpm) {
            prevState.metronomeMachine.bpm = newState.bpm;
        }
        if (newState.metroVolume !== prevState.metroVolumt) {
            prevState.metronomeMachine.volume = newState.metroVolume;
        }

        if (!isEqual(prevState, newState)) {
            return newState;
        } else {
            return null;
        }
    }

    constructor(props, context) {
        super(props, context);

        const metronomeMachine = new MetronomeMachine();
        this.service = metronomeMachine.onTransition(
            this.updateState.bind(this)
        );
        metronomeMachine.volume = props.metroVolume;
        metronomeMachine.bpm = props.bpm;

        this.state = {
            metronomeMachine,
            ...this.props,
        };
    }

    updateState(metronomeMachineState) {
        const currentState = metronomeMachineState.value;
        const { beat, timer } = metronomeMachineState.context;
        const currentMachineState = { currentState, beat, timer };
        if (!isEqual(metroState, currentMachineState)) {
            this.props.notifyMetronomeEvent(currentState, beat, timer);
            metroState.currentState = currentState;
            metroState.beat = beat;
            metroState.timer = timer;
        }
    }

    componentDidMount() {
        this.service.start();
    }

    componentWillUnmount() {
        this.service.stop();
    }

    render() {
        return '';
    }
}

const mapStateToProps = ({
    playerLoopDuration,
    playerMachineState,
    playHeadIndex,
    playHeadLength,
    playHeadRepeat,
    bpm,
    metroVolume,
}) => {
    return {
        playerLoopDuration,
        playerMachineState,
        playHeadIndex,
        playHeadLength,
        playHeadRepeat,
        bpm,
        metroVolume,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        notifyMetronomeEvent: (engineState, beat, timer) =>
            dispatch({
                type: `METRONOME_EVENT`,
                engineState,
                beat,
                timer,
            }),
    };
};

Metronome.propTypes = {
    bpm: PropTypes.number,
    playerLoopDuration: PropTypes.string,
    playerMachineState: PropTypes.string,
    notifyMetronomeEvent: PropTypes.func.isRequired,
    playHeadIndex: PropTypes.number,
    playHeadLength: PropTypes.number,
    playHeadRepeat: PropTypes.bool,
    playHeadComplete: PropTypes.number,
    metroVolume: PropTypes.number,
};

Metronome.defaultProps = {
    metroVolume: -18,
    bpm: 120,
    playHeadIndex: 0,
    playHeadLength: 0,
};

export default connect(mapStateToProps, mapDispatchToProps)(Metronome);
